import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import BackButton from '@/components/BackButton';
import ContentSpinner from '@/components/ContentSpinner';
import { Controls, ControlsButton } from '@/components/Controls';
import FullScreenButton from '@/components/FullScreenButton';
import { PageHeading } from '@/components/Layout/Parts';
import { TableHeader } from '@/components/Table';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { CoverImagesProjectResource } from '@/types/resources';
import { api } from '@/utils/api';
import { CoverImagesImageStatus } from '@/utils/enums';
import { Dispatch, RootState } from '@/utils/store';

import AiGeneratedCovers from './components/AiGeneratedCovers';
import ArticleBox from './components/ArticleBox';
import Generator from './components/Generator';
import ImagesBox from './components/ImagesBox';

const usersEmails = [
  'greta@boredpanda.com',
  'andrius.sadausk@gmail.com',
  'tony@boredpanda.com',
];

const CoverImagesSingle = () => {
  const userEmail = useSelector((state: RootState) => state.auth.user?.email);

  const [imagesStatus, setImagesStatus] = useState(
    CoverImagesImageStatus.PENDING,
  );

  const { id } = useParams();

  const alerts = useAlerts();

  const navigate = useNavigate();

  const dispatch = useDispatch<Dispatch>();

  const { isLoading, data: project } = useQuery(
    ['cover_images_project', { id }],
    () =>
      api.get<never, CoverImagesProjectResource>(
        `/v1/cover-images/cover-images-projects/${id}`,
      ),
    {
      staleTime: 60 * 1000,
      retry: 1,
      onError: async (err) => {
        if ((err as any)?.response?.status === 404) {
          try {
            const newArticleData = await api.get<
              never,
              {
                title: string;
                slug: string;
                id: number;
                date_created: number;
                author_email: string;
                post_status: string;
                author_name: string;
                is_open_list: boolean;
              }
            >(`/v1/cover-images/single-cover-images-bp-article`, {
              params: {
                article_id: id,
              },
            });

            dispatch.coverImages.setNewArticle({
              title: newArticleData.title,
              slug: newArticleData.slug,
              id: newArticleData.id,
              date_created: DateTime.fromSeconds(newArticleData.date_created, {
                zone: 'UTC',
              }).toISO(),
              editor: newArticleData.author_email,
              is_open_list: newArticleData.is_open_list,
            });

            navigate('/general/cover-images/generate/one');
          } catch (error) {
            alerts.error('Error fetching alternative project data.');
          }
        } else {
          alerts.error('An unexpected error occurred.');
        }
      },
    },
  );

  if (isLoading || !project) {
    return <ContentSpinner />;
  }

  const pendingImages = project.images?.filter(
    (image) => image.meta?.status === CoverImagesImageStatus.PENDING,
  );

  const fixingImages = project.images?.filter(
    (image) =>
      image.meta?.status === CoverImagesImageStatus.FIXING ||
      image.meta?.keepOriginal,
  );

  const approvedImages = project.images?.filter(
    (image) => image.meta?.status === CoverImagesImageStatus.APPROVED,
  );

  const sentImages = project.images?.filter(
    (image) => image.meta?.status === CoverImagesImageStatus.SENT,
  );

  return (
    <>
      <Text $type="caption" $color="gray">
        BORED PANDA
      </Text>
      <PageHeading>Cover Images</PageHeading>
      <BackButton />
      <ArticleBox article={project} />
      {userEmail && usersEmails.includes(userEmail) && (
        <AiGeneratedCovers project={project} />
      )}
      <TableHeader>
        <Controls>
          <ControlsButton
            active={imagesStatus === CoverImagesImageStatus.PENDING}
            onClick={() => setImagesStatus(CoverImagesImageStatus.PENDING)}
          >
            Pending
          </ControlsButton>
          <ControlsButton
            active={imagesStatus === CoverImagesImageStatus.FIXING}
            onClick={() => setImagesStatus(CoverImagesImageStatus.FIXING)}
          >
            To Fix
          </ControlsButton>
          <ControlsButton
            active={imagesStatus === CoverImagesImageStatus.APPROVED}
            onClick={() => setImagesStatus(CoverImagesImageStatus.APPROVED)}
          >
            Approved
          </ControlsButton>
          <ControlsButton
            active={imagesStatus === CoverImagesImageStatus.SENT}
            onClick={() => setImagesStatus(CoverImagesImageStatus.SENT)}
          >
            Sent to BP
          </ControlsButton>
        </Controls>
        <FullScreenButton />
      </TableHeader>
      {imagesStatus === CoverImagesImageStatus.PENDING && (
        <ImagesBox
          images={pendingImages}
          status={CoverImagesImageStatus.PENDING}
          comment={project.comment}
          sentImagesCount={project.sentCoversCount}
        />
      )}
      {imagesStatus === CoverImagesImageStatus.FIXING && (
        <ImagesBox
          images={fixingImages}
          status={CoverImagesImageStatus.FIXING}
          comment={project.comment}
          sentImagesCount={project.sentCoversCount}
        />
      )}
      {imagesStatus === CoverImagesImageStatus.APPROVED && (
        <ImagesBox
          images={approvedImages}
          status={CoverImagesImageStatus.APPROVED}
          sentImagesCount={project.sentCoversCount}
        />
      )}
      {imagesStatus === CoverImagesImageStatus.SENT && (
        <ImagesBox images={sentImages} status={CoverImagesImageStatus.SENT} />
      )}
      <Generator />
    </>
  );
};

export default CoverImagesSingle;
